import * as React from "react";
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, subDays } from "date-fns";
import { Combobox, Input } from "@/components/ui";

export type ReviewDatePreset =
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "two_months_ago"
  | "ytd"
  | "custom";

const REVIEW_DATE_PRESET_LABELS: Record<ReviewDatePreset, string> = {
  last_7_days: "Last 7 Days",
  last_30_days: "Last 30 Days",
  this_month: "This Month",
  last_month: "Last Month",
  two_months_ago: "Two Months Ago",
  ytd: "Year to Date",
  custom: "Custom Range",
};

const REVIEW_DATE_PRESET_OPTIONS = Object.keys(REVIEW_DATE_PRESET_LABELS) as ReviewDatePreset[];

export function resolveReviewDatePreset(
  preset: ReviewDatePreset | null,
  current?: { startDate: string | null; endDate: string | null }
): { startDate: string | null; endDate: string | null } {
  if (!preset) return { startDate: null, endDate: null };

  const now = new Date();
  switch (preset) {
    case "this_month":
      return {
        startDate: format(startOfMonth(now), "yyyy-MM-dd"),
        endDate: format(now, "yyyy-MM-dd"),
      };
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: format(startOfMonth(lastMonth), "yyyy-MM-dd"),
        endDate: format(endOfMonth(lastMonth), "yyyy-MM-dd"),
      };
    }
    case "two_months_ago": {
      const twoMonthsAgo = subMonths(now, 2);
      return {
        startDate: format(startOfMonth(twoMonthsAgo), "yyyy-MM-dd"),
        endDate: format(endOfMonth(twoMonthsAgo), "yyyy-MM-dd"),
      };
    }
    case "last_7_days":
      return {
        startDate: format(subDays(now, 7), "yyyy-MM-dd"),
        endDate: format(now, "yyyy-MM-dd"),
      };
    case "last_30_days":
      return {
        startDate: format(subDays(now, 30), "yyyy-MM-dd"),
        endDate: format(now, "yyyy-MM-dd"),
      };
    case "ytd":
      return {
        startDate: format(startOfYear(now), "yyyy-MM-dd"),
        endDate: format(now, "yyyy-MM-dd"),
      };
    case "custom":
      if (current?.startDate || current?.endDate) {
        return { startDate: current.startDate ?? null, endDate: current.endDate ?? null };
      }
      return {
        startDate: format(subDays(now, 30), "yyyy-MM-dd"),
        endDate: format(now, "yyyy-MM-dd"),
      };
  }
}

type ReviewTimeFrameFilterProps = {
  preset: ReviewDatePreset | null;
  startDate: string | null;
  endDate: string | null;
  onChange: (next: { preset: ReviewDatePreset | null; startDate: string | null; endDate: string | null }) => void;
  className?: string;
  showLabel?: boolean;
};

export function ReviewTimeFrameFilter({
  preset,
  startDate,
  endDate,
  onChange,
  className,
  showLabel = true,
}: ReviewTimeFrameFilterProps) {
  const handlePresetChange = (value: string | null | undefined) => {
    const nextPreset = (value as ReviewDatePreset | null | undefined) || null;
    if (!nextPreset) {
      onChange({ preset: null, startDate: null, endDate: null });
      return;
    }
    const dates = resolveReviewDatePreset(nextPreset, { startDate, endDate });
    onChange({ preset: nextPreset, ...dates });
  };

  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        {showLabel ? (
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Time Frame</label>
        ) : null}
        <Combobox
          items={REVIEW_DATE_PRESET_OPTIONS}
          value={preset}
          onValueChange={handlePresetChange}
          itemToStringLabel={item => REVIEW_DATE_PRESET_LABELS[item as ReviewDatePreset]}
          placeholder="All Time"
          showClear
        />
      </div>

      {preset === "custom" ? (
        <div className="grid grid-cols-2 gap-3 mt-3 animate-in slide-in-from-top-1 duration-200">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Date</label>
            <Input
              type="date"
              value={startDate ?? ""}
              onChange={e => {
                onChange({
                  preset: "custom",
                  startDate: e.target.value || null,
                  endDate,
                });
              }}
              className="h-9 text-xs border-slate-200 shadow-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Date</label>
            <Input
              type="date"
              value={endDate ?? ""}
              onChange={e => {
                onChange({
                  preset: "custom",
                  startDate,
                  endDate: e.target.value || null,
                });
              }}
              className="h-9 text-xs border-slate-200 shadow-sm"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
